import React, { useState, useEffect, useMemo, useRef } from 'react';
import { formatPhoneDisplay, formatPhoneForTel } from '@/utils/phoneFormatter';
import { X, User, BarChart3, Clock, Activity, Bell, Volume2, Phone, ShoppingCart, FileText, Settings, Building2, Search, MapPin, Send, CheckCircle, XCircle, Loader2, School, Bot, Mic, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { Lead } from '@/types/leads/Table';
import DynamicLeadForm from '../Forms/Leads/DynamicLeadForm';
import AdminTrackingForm from '../Forms/Leads/AdminTrackingForm';
import ReminderForm from '../Forms/Leads/ReminderForm';
import { optimizedApi } from '@/utils/api/optimized-api';
import { getLeadEvents } from '@/actions/data-fetch-activities';
import { getLeadDetails } from '@/actions/lead-manager';
import { format } from 'date-fns';
import InitiateCallModal from '../OutboundCalling/InitiateCallModal';
import { fetchOutboundConfig, OutboundCallConfig } from '@/utils/outbound-calling';
import { useCallingState } from '@/contexts/CallingContext';
import { showToast } from '@/components/ui/Toast';
import LeadProfileSections from './LeadProfileSections';
import { LeadProfileSettings } from '@/types/leads/Profile';

// Safe date formatting helper
const safeFormatDate = (dateValue: any, formatString: string, fallback: string = 'Invalid date') => {
    try {
        if (!dateValue) return fallback;
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return fallback;
        return format(date, formatString);
    } catch (error) {
        return fallback;
    }
};

interface UserOption {
    value: string;
    label: string;
    email?: string;
    role?: string;
}

interface LeadDetailsSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    lead: Lead | null;
    onUpdate?: () => void;
    activeUsersForDropdown?: UserOption[];
    isAdminView?: boolean; // When true, shows Lead Data tab and Admin Form tab
    defaultTab?: 'form' | 'leaddata' | 'adminform' | 'lineitems' | 'marketing' | 'remarks' | 'activity' | 'reminders' | 'attachschool' | 'assignschool' | 'aicalls' | 'runocalls' | 'profile';
}

interface RunoCall {
    id: number;
    uuid: string;
    runo_call_id: string;
    phone: string;
    agent_phone: string;
    agent_name: string;
    call_status: string | null;
    call_direction: string;
    call_duration_sec: number | null;
    recording_url: string | null;
    called_at: string | null;
    created_at: string;
}

interface AICall {
    id: number;
    uuid: string;
    call_id: string;
    agent_id: string;
    agent_name: string;
    phone_number: string;
    status: string;
    direction: string;
    duration: number;
    summary: string;
    transcript: string;
    recording_url: string;
    ended_reason: string;
    cost: number;
    call_started_at: string;
    call_ended_at: string;
    created_at: string;
    updated_at: string;
}


interface Remark {
    remark_id: number;
    uuid: string;
    remark: string;
    remark_type: string;
    date_remark: string;
    formattedDate: string;
    userDisplay: string;
    next_follow_up?: string;
    source?: string;
    user?: {
        userId: number;
        uuid: string;
        userName: string;
    };
}

interface ActivityLog {
    id: string;
    user_name: string;
    action: string;
    details: string;
    created_at: string;
    log_type?: string;
    log_action?: string;
    log_date?: string;
    log_data?: any;
}

interface LeadReminder {
    uuid: string;
    event_title: string;
    db_start: string;
    alert_before: string;
    created_at: string;
    lead_uuid: string;
}

interface LineItem {
    line_item_id: number;
    lead_id_fk: number;
    lead_uuid: string;
    title: string;
    variant_title?: string;
    variant_price: number;
    quantity: number;
    sku?: string;
    product_id?: string;
    variant_id?: string;
    line_item_total: number;
    created_at: string;
    updated_at: string;
}

const LeadDetailsSidebar: React.FC<LeadDetailsSidebarProps> = ({
    isOpen,
    onClose,
    lead,
    onUpdate,
    activeUsersForDropdown = [],
    isAdminView = false,
    defaultTab
}) => {
    const [activeTab, setActiveTab] = useState<'form' | 'leaddata' | 'adminform' | 'lineitems' | 'marketing' | 'remarks' | 'activity' | 'reminders' | 'attachschool' | 'assignschool' | 'aicalls' | 'runocalls' | 'profile'>(defaultTab || 'form');
    const [remarks, setRemarks] = useState<Remark[]>([]);
    const [activities, setActivities] = useState<ActivityLog[]>([]);
    const [reminders, setReminders] = useState<LeadReminder[]>([]);
    const [lineItems, setLineItems] = useState<LineItem[]>([]);
    const [aiCalls, setAiCalls] = useState<AICall[]>([]);
    const [runoCalls, setRunoCalls] = useState<RunoCall[]>([]);
    const [expandedCallId, setExpandedCallId] = useState<number | null>(null);
    const [newRemark, setNewRemark] = useState('');
    // Full lead details from API (for admin view)
    const [fullLeadDetails, setFullLeadDetails] = useState<any>(null);
    const [loadingFullDetails, setLoadingFullDetails] = useState(false);

    // Attach School state
    const [popularCities, setPopularCities] = useState<{cityId: number, cityName: string}[]>([]);
    const [selectedCity, setSelectedCity] = useState<number | null>(null);
    const [schoolSearchQuery, setSchoolSearchQuery] = useState('');
    const [schoolSearchResults, setSchoolSearchResults] = useState<any[]>([]);
    const [searchingSchools, setSearchingSchools] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    // Assign School state
    const [existingAssignments, setExistingAssignments] = useState<any[]>([]);
    const [loadingAssignments, setLoadingAssignments] = useState(false);
    const [selectedSchoolsToAssign, setSelectedSchoolsToAssign] = useState<number[]>([]);
    const [assigningSchools, setAssigningSchools] = useState(false);
    const [nearbySchools, setNearbySchools] = useState<any[]>([]);
    const [loadingNearbySchools, setLoadingNearbySchools] = useState(false);
    const [nearbySearchType, setNearbySearchType] = useState<string>('');
    const [nearbySchoolsFilter, setNearbySchoolsFilter] = useState<string>('');
    const [showClientOnly, setShowClientOnly] = useState<boolean>(false);
    const [showClaimedOnly, setShowClaimedOnly] = useState<boolean>(false);

    // Outbound calling state
    const [outboundConfig, setOutboundConfig] = useState<OutboundCallConfig | null>(null);
    const [isCallModalOpen, setIsCallModalOpen] = useState(false);
    const { isCallInProgress, setCallInProgress } = useCallingState();

    // Get client industry from localStorage
    const getClientIndustry = (): string => {
        try {
            const userData = JSON.parse(localStorage.getItem('crm_user') || '{}');
            const data = userData.data || userData;
            return data.client?.industry || '';
        } catch (error) {
            console.error('Error getting client industry:', error);
            return '';
        }
    };

    const isEcommerce = getClientIndustry().toLowerCase() === 'ecommerce';

    // Extended lead-profile sections (VIS-style Child Details / Communication / Qualification & Status).
    // Off by default for every client — only fetched once, tab only appears if a client has opted in.
    const [leadProfileSettings, setLeadProfileSettings] = useState<LeadProfileSettings | null>(null);
    useEffect(() => {
        optimizedApi.get<LeadProfileSettings>('/lead-profile/settings').then((res) => {
            if (res.success && res.data) setLeadProfileSettings(res.data);
        }).catch(() => { /* extended profile is optional — silently unavailable */ });
    }, []);
    const showLeadProfileTab = !!leadProfileSettings?.is_active && (leadProfileSettings?.sections_enabled?.length || 0) > 0;

    // Vista (CRM account 11008) names its two forms so counsellors can tell them apart:
    // the native Lead Form is the one Yellowslate fills, the Extended Profile is Vista's own.
    const VISTA_CLIENT_ID = 11008;
    const isVista = (() => {
        try {
            const user = JSON.parse(localStorage.getItem('crm_user') || 'null');
            return (user?.data?.data?.client_id_fk || user?.data?.client_id_fk || user?.client_id_fk) === VISTA_CLIENT_ID;
        } catch {
            return false;
        }
    })();
    const leadFormTabLabel = isVista && !isAdminView ? 'Yellowslate Form' : 'Lead Form';
    const profileTabLabel = isVista && !isAdminView ? 'Vista Form' : 'Extended Profile';
    // Vista counsellors work in the Vista Form; the Yellowslate Form is view-only for them and its
    // fields carry the Vista Form's names (or a "YS" prefix where the Vista Form has its own field).
    const vistaYsFormReadOnly = isVista && !isAdminView;
    const VISTA_YS_FORM_LABELS: Record<string, string> = {
        name: 'Contact Name',
        source: 'YS Source',
        status: 'YS Status',
        phone: 'Contact Phone',
        email: 'Contact Email',
        grade: 'Grade',
    };
    const openedOnVistaFormRef = useRef(false);

    // Filter remarks to exclude audio remarks with source explicitly set to "None"
    // Also exclude admin remarks (remark_type: 'admin' or 'admin_user') on client view - clients should not see them
    const filteredRemarks = useMemo(() => {
        return remarks.filter(remark => {
            // Exclude admin remarks on client view only - clients should not see these
            if (!isAdminView && (remark.remark_type === 'admin' || remark.remark_type === 'admin_user')) {
                return false;
            }
            // For audio remarks, only exclude if source is explicitly "None"
            if (remark.remark_type === 'audio') {
                return remark.source !== 'None';
            }
            // Include all other remarks
            return true;
        });
    }, [remarks, isAdminView]);

    // Track which tabs have been loaded to avoid unnecessary API calls
    const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set(['form'])); // Form is loaded by default
    const [loadingTabs, setLoadingTabs] = useState<Set<string>>(new Set());

    // Reset state when modal closes or new lead is selected
    useEffect(() => {
        if (lead && isOpen) {
            // Reset state for new lead
            // Use prop defaultTab if provided, otherwise use form/leaddata based on view
            const initialTab = defaultTab || (isAdminView ? 'leaddata' : 'form');
            setLoadedTabs(new Set([initialTab]));
            setLoadingTabs(new Set());
            setRemarks([]);
            setActivities([]);
            setReminders([]);
            setLineItems([]);
            setAiCalls([]);
            setRunoCalls([]);
            setExpandedCallId(null);
            setFullLeadDetails(null);
            setActiveTab(initialTab);
            openedOnVistaFormRef.current = false;

            // DynamicLeadForm will fetch full lead details using leadUuid
            // We don't pass leadData to ensure it fetches complete data from API
        }
    }, [lead?.uuid, isOpen, isAdminView, defaultTab]);

    // Vista users open a lead (e.g. by clicking its phone number) straight on the Vista Form.
    // Lead profile settings load after the modal opens, so switch once per opened lead —
    // later clicks on Yellowslate Form are left alone.
    useEffect(() => {
        if (!isOpen || defaultTab || !isVista || isAdminView || !showLeadProfileTab) return;
        if (openedOnVistaFormRef.current || activeTab !== 'form') return;
        openedOnVistaFormRef.current = true;
        setActiveTab('profile');
    }, [isOpen, lead?.uuid, defaultTab, isVista, isAdminView, showLeadProfileTab, activeTab]);

    // Fetch full lead details for admin view
    // Reset fullLeadDetails when lead changes
    useEffect(() => {
        setFullLeadDetails(null);
    }, [lead?.uuid]);

    useEffect(() => {
        const fetchFullLeadDetails = async () => {
            if (!lead?.uuid || !isAdminView) return;

            setLoadingFullDetails(true);
            try {
                const response = await getLeadDetails(lead.uuid);
                console.log('📋 Full lead details response:', response);
                if (response.success && response.data) {
                    console.log('📋 ClientInfo:', (response.data as any).clientInfo);
                    setFullLeadDetails(response.data);
                }
            } catch (error) {
                console.error('Error fetching full lead details:', error);
            } finally {
                setLoadingFullDetails(false);
            }
        };

        if (isAdminView && isOpen && lead?.uuid) {
            fetchFullLeadDetails();
        }
    }, [lead?.uuid, isAdminView, isOpen]);

    // Load outbound config on mount (from cache or API with deduplication)
    useEffect(() => {
        const loadOutboundConfig = async () => {
            const config = await fetchOutboundConfig(() =>
                optimizedApi.get('/outbound-call/config')
            );
            setOutboundConfig(config);
        };
        loadOutboundConfig();
    }, []);

    // Handle tab switching with lazy loading
    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId as any);

        // Load data for the tab if not already loaded
        switch (tabId) {
            case 'lineitems':
                fetchLineItemsOnly();
                break;
            case 'remarks':
                fetchRemarksOnly();
                break;
            case 'activity':
                fetchActivitiesOnly();
                break;
            case 'marketing':
                fetchRemindersOnly();
                break;
            case 'reminders':
                fetchRemindersOnly();
                break;
            case 'assignschool':
                fetchSchoolAssignments();
                fetchNearbySchoolsForAssign();
                break;
            case 'aicalls':
                fetchAiCallsOnly();
                break;
            case 'runocalls':
                fetchRunoCallsOnly();
                break;
            // 'form' doesn't need additional data loading
        }
    };

    // Fetch line items for line items tab
    const fetchLineItemsOnly = async () => {
        if (!lead || loadedTabs.has('lineitems')) return;

        setLoadingTabs(prev => new Set([...prev, 'lineitems']));

        try {
            const lineItemsRes = await optimizedApi.get<LineItem[]>(`/leads/line-items/${lead.uuid}`);
            if (lineItemsRes.success) {
                setLineItems(lineItemsRes.data || []);
                console.log('✅ Line items loaded:', lineItemsRes.data?.length || 0, 'items');
            } else {
                console.log('⚠️ No line items found or endpoint error');
                setLineItems([]);
            }
        } catch (error) {
            console.log('⚠️ Error fetching line items:', error);
            setLineItems([]);
        } finally {
            setLoadedTabs(prev => new Set([...prev, 'lineitems']));
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('lineitems');
                return newSet;
            });
        }
    };

    // Fetch remarks for remarks tab
    const fetchRemarksOnly = async () => {
        if (!lead || loadedTabs.has('remarks')) return;

        setLoadingTabs(prev => new Set([...prev, 'remarks']));
        try {
            const remarksRes = await optimizedApi.get<Remark[]>(`/leads/remarks/${lead.uuid}`);
            if (remarksRes.success) {
                setRemarks(remarksRes.data || []);
                console.log('✅ Remarks loaded:', remarksRes.data?.length || 0, 'remarks');
            } else {
                console.log('⚠️ No remarks found or endpoint error');
                setRemarks([]);
            }
            setLoadedTabs(prev => new Set([...prev, 'remarks']));
        } catch (error) {
            console.log('⚠️ Error fetching remarks:', error);
            setRemarks([]);
        } finally {
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('remarks');
                return newSet;
            });
        }
    };

    // Fetch activities for activity tab
    const fetchActivitiesOnly = async () => {
        if (!lead || loadedTabs.has('activity')) return;

        setLoadingTabs(prev => new Set([...prev, 'activity']));
        try {
            const activitiesRes = await optimizedApi.get<ActivityLog[]>(`/leads/activity/${lead.uuid}`);
            if (activitiesRes.success) {
                setActivities(activitiesRes.data || []);
                console.log('✅ Activities loaded:', activitiesRes.data?.length || 0, 'activities');
            }
            setLoadedTabs(prev => new Set([...prev, 'activity']));
        } catch (error) {
            console.log('⚠️ Error fetching activities:', error);
        } finally {
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('activity');
                return newSet;
            });
        }
    };

    // Fetch reminders/events for marketing and reminders tabs
    const fetchRemindersOnly = async () => {
        if (!lead || (loadedTabs.has('marketing') && loadedTabs.has('reminders'))) return;

        // Determine which tab we're loading for
        const currentTab = activeTab === 'reminders' ? 'reminders' : 'marketing';
        setLoadingTabs(prev => new Set([...prev, currentTab]));
        try {
            const remindersRes = await getLeadEvents(lead.uuid);
            // Handle both ApiResponse and fallback { data: [] } response
            if ('success' in remindersRes && remindersRes.success) {
                setReminders((remindersRes.data as LeadReminder[]) || []);
                console.log('✅ Reminders loaded:', (remindersRes.data as LeadReminder[])?.length || 0, 'reminders');
            } else if ('data' in remindersRes) {
                // Fallback case when endpoint returns { data: [] }
                setReminders((remindersRes.data as LeadReminder[]) || []);
                console.log('✅ Reminders loaded (fallback):', (remindersRes.data as LeadReminder[])?.length || 0, 'reminders');
            } else {
                console.log('⚠️ No reminders found');
                setReminders([]);
            }
            // Mark both marketing and reminders as loaded since they share the same data
            setLoadedTabs(prev => new Set([...prev, 'marketing', 'reminders']));
        } catch (error) {
            console.log('⚠️ Error fetching reminders:', error);
            setReminders([]);
        } finally {
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete(currentTab);
                return newSet;
            });
        }
    };

    // Fetch AI calls for aicalls tab (admin only)
    const fetchAiCallsOnly = async () => {
        if (!lead || loadedTabs.has('aicalls')) return;

        setLoadingTabs(prev => new Set([...prev, 'aicalls']));
        try {
            const response = await optimizedApi.get<{ calls: AICall[], total: number }>(`/leads/ai-calls/${lead.uuid}`);
            if (response.success && response.data) {
                setAiCalls(response.data.calls || []);
                console.log('✅ AI Calls loaded:', response.data.calls?.length || 0, 'calls');
            } else {
                console.log('⚠️ No AI calls found');
                setAiCalls([]);
            }
            setLoadedTabs(prev => new Set([...prev, 'aicalls']));
        } catch (error) {
            console.log('⚠️ Error fetching AI calls:', error);
            setAiCalls([]);
        } finally {
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('aicalls');
                return newSet;
            });
        }
    };

    // Fetch Runo calls for runocalls tab (admin only)
    const fetchRunoCallsOnly = async () => {
        if (!lead || loadedTabs.has('runocalls')) return;

        setLoadingTabs(prev => new Set([...prev, 'runocalls']));
        try {
            const response = await optimizedApi.get<{ calls: RunoCall[], total: number }>(`/leads/runo-calls/${lead.uuid}`);
            if (response.success && response.data) {
                setRunoCalls(response.data.calls || []);
                console.log('✅ Runo Calls loaded:', response.data.calls?.length || 0, 'calls');
            } else {
                console.log('⚠️ No Runo calls found');
                setRunoCalls([]);
            }
            setLoadedTabs(prev => new Set([...prev, 'runocalls']));
        } catch (error) {
            console.log('⚠️ Error fetching Runo calls:', error);
            setRunoCalls([]);
        } finally {
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('runocalls');
                return newSet;
            });
        }
    };

    // Format duration in seconds to mm:ss
    const formatDuration = (seconds: number): string => {
        if (!seconds) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAddRemark = async () => {
        if (!lead || !newRemark.trim()) return;

        try {
            interface AddRemarkResponse {
                remarkId: number;
                remarkUuid: string;
                date: string;
                userDisplay: string;
            }

            const response = await optimizedApi.post<AddRemarkResponse>('/leads/remarks', {
                lead_uuid: lead.uuid,
                remark: newRemark
            });

            if (response.success && response.data) {
                // Create a remark object that matches the expected frontend format
                const newRemarkObj: Remark = {
                    remark_id: response.data.remarkId,
                    uuid: response.data.remarkUuid,
                    date_remark: response.data.date,
                    remark: newRemark,
                    remark_type: 'text',
                    formattedDate: response.data.date,
                    userDisplay: response.data.userDisplay,
                };
                setRemarks([newRemarkObj, ...remarks]);
                setNewRemark('');
                console.log('✅ Remark added successfully');
            } else {
                console.log('⚠️ Failed to add remark:', response.message);
            }
        } catch (error) {
            console.error('❌ Error adding remark:', error);
        }
    };

    // Outbound calling handlers
    const handleInitiateCall = (event: React.MouseEvent) => {
        event.stopPropagation();

        if (isCallInProgress) {
            showToast('Another call is already in progress', 'error');
            return;
        }

        if (!lead?.phone) {
            showToast('No phone number available for this lead', 'error');
            return;
        }

        setIsCallModalOpen(true);
        setCallInProgress(true);
    };

    const handleCloseCallModal = () => {
        setIsCallModalOpen(false);
        setCallInProgress(false);
    };

    // Fetch popular cities for school search
    const fetchPopularCities = async () => {
        if (popularCities.length > 0) return; // Already loaded
        setLoadingCities(true);
        try {
            const response = await optimizedApi.get<{ cities: { cityId: number; cityName: string }[] }>('/schools/popular-cities');
            if (response.success && response.data?.cities) {
                setPopularCities(response.data.cities);
            }
        } catch (error) {
            console.error('Error fetching popular cities:', error);
        } finally {
            setLoadingCities(false);
        }
    };

    // Search schools by city and query
    const searchSchools = async () => {
        if (!schoolSearchQuery || schoolSearchQuery.length < 2) {
            setSchoolSearchResults([]);
            return;
        }
        setSearchingSchools(true);
        try {
            const params: any = { search_term: schoolSearchQuery, limit: 20 };
            if (selectedCity) {
                params.city = selectedCity;
            }
            const response = await optimizedApi.post<{ schools: any[] }>('/schools/search', params);
            if (response.success && response.data?.schools) {
                setSchoolSearchResults(response.data.schools);
            } else {
                setSchoolSearchResults([]);
            }
        } catch (error) {
            console.error('Error searching schools:', error);
            setSchoolSearchResults([]);
        } finally {
            setSearchingSchools(false);
        }
    };

    // Handle attach school button click
    const handleAttachSchool = async (schoolId: number) => {
        if (!lead?.uuid) {
            showToast('Lead UUID is required', 'error');
            return;
        }

        try {
            const response = await optimizedApi.post('/leads/attach_school', {
                uuid: lead.uuid,
                school_id: schoolId
            });

            if (response.success) {
                const schoolName = (response.data as any)?.school_name || 'School';
                showToast(`School attached successfully: ${schoolName}`, 'success');

                // Refresh full lead details to update hasSchoolAttached
                const refreshedResponse = await getLeadDetails(lead.uuid);
                if (refreshedResponse.success && refreshedResponse.data) {
                    setFullLeadDetails(refreshedResponse.data);
                }

                // Refresh parent component data
                onUpdate?.();

                // Switch to lead data tab after successful attachment
                setActiveTab('leaddata');
            } else {
                showToast(response.message || 'Failed to attach school', 'error');
            }
        } catch (error: any) {
            console.error('Error attaching school:', error);
            showToast(error.message || 'Failed to attach school', 'error');
        }
    };

    // Fetch existing school assignments for this lead
    const fetchSchoolAssignments = async () => {
        if (!lead?.uuid) return;
        setLoadingAssignments(true);
        try {
            const response = await optimizedApi.get<{ assignments: any[] }>(`/leads/school_assignments/${lead.uuid}`);
            if (response.success && response.data?.assignments) {
                setExistingAssignments(response.data.assignments);
            } else {
                setExistingAssignments([]);
            }
        } catch (error) {
            console.error('Error fetching school assignments:', error);
            setExistingAssignments([]);
        } finally {
            setLoadingAssignments(false);
        }
    };

    // Fetch nearby schools for assignment (client + claimed schools)
    const fetchNearbySchoolsForAssign = async () => {
        if (!lead?.uuid) return;
        setLoadingNearbySchools(true);
        try {
            const response = await optimizedApi.post<{ schools: any[], search_type: string, message?: string }>('/schools/nearby-for-assign', { lead_uuid: lead.uuid });
            if (response.success && response.data?.schools) {
                // Filter out already assigned schools
                const assignedSchoolIds = existingAssignments.map(a => a.school_id);
                const filteredSchools = response.data.schools.filter(
                    (school: any) => !assignedSchoolIds.includes(school.slate_id)
                );
                setNearbySchools(filteredSchools);
                setNearbySearchType(response.data.search_type || '');
            } else {
                setNearbySchools([]);
                setNearbySearchType('');
            }
        } catch (error) {
            console.error('Error fetching nearby schools:', error);
            setNearbySchools([]);
            setNearbySearchType('');
        } finally {
            setLoadingNearbySchools(false);
        }
    };

    // Toggle school selection for assignment
    const toggleSchoolSelection = (schoolId: number) => {
        setSelectedSchoolsToAssign(prev =>
            prev.includes(schoolId)
                ? prev.filter(id => id !== schoolId)
                : [...prev, schoolId]
        );
    };

    // Handle assign schools
    const handleAssignSchools = async () => {
        if (!lead?.uuid || selectedSchoolsToAssign.length === 0) {
            showToast('Please select at least one school to assign', 'error');
            return;
        }

        setAssigningSchools(true);
        try {
            const response = await optimizedApi.post('/leads/assign_to_schools', {
                uuid: lead.uuid,
                school_ids: selectedSchoolsToAssign
            });

            if (response.success) {
                const data = response.data as any;
                showToast(`Lead assigned to ${data.total_assigned} school(s)`, 'success');

                // Clear selection and refresh assignments
                setSelectedSchoolsToAssign([]);
                await fetchSchoolAssignments();

                // Refresh lead details
                onUpdate?.();
            } else {
                showToast(response.message || 'Failed to assign schools', 'error');
            }
        } catch (error: any) {
            console.error('Error assigning schools:', error);
            showToast(error.message || 'Failed to assign schools', 'error');
        } finally {
            setAssigningSchools(false);
        }
    };

    // Check if school is attached
    const hasSchoolAttached = fullLeadDetails?.clientInfo?.school;

    // Define tabs based on admin view or regular view
    const tabs = isAdminView
        ? [
            { id: 'leaddata', label: 'Lead Data', icon: FileText },
            { id: 'adminform', label: 'Admin Form', icon: Settings },
            // Show Attach School tab only when no school is attached
            ...(!hasSchoolAttached ? [{ id: 'attachschool', label: 'Attach School', icon: Building2 }] : []),
            { id: 'assignschool', label: 'Assign School', icon: Send },
            { id: 'remarks', label: 'Remarks', icon: Clock },
            { id: 'activity', label: 'Activity', icon: Activity },
            { id: 'aicalls', label: 'AI Calls', icon: Bot },
            { id: 'runocalls', label: 'Runo Calls', icon: Phone },
            ...(showLeadProfileTab ? [{ id: 'profile', label: 'Extended Profile', icon: Layers }] : [])
        ]
        : [
            { id: 'form', label: leadFormTabLabel, icon: User },
            ...(isEcommerce ? [{ id: 'lineitems', label: 'Line Items', icon: ShoppingCart }] : []),
            ...(showLeadProfileTab ? [{ id: 'profile', label: profileTabLabel, icon: Layers }] : []),
            { id: 'marketing', label: 'Marketing', icon: BarChart3 },
            { id: 'remarks', label: 'Remarks', icon: Clock },
            { id: 'activity', label: 'Activity', icon: Activity },
            { id: 'reminders', label: 'Reminders', icon: Bell }
        ];

    if (!isOpen || !lead) return null;

    return (
        <>
            {/* Full Screen Modal */}
            <div
                className={`fixed inset-0 bg-white z-[70] transform transition-transform duration-300 ease-in-out ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 shrink-0">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">
                                {isAdminView ? 'Admin Lead View' : 'Lead Details'}
                            </h2>
                            <div className="flex items-center gap-3 mt-1">
                                <p className="text-sm text-gray-600 font-medium">{lead.name}</p>
                                <span className="text-gray-300">|</span>
                                <p className="text-sm text-gray-600">{formatPhoneDisplay(lead.phone, lead.country_code)}</p>
                                {lead.email && (
                                    <>
                                        <span className="text-gray-300">|</span>
                                        <p className="text-sm text-gray-500">{lead.email}</p>
                                    </>
                                )}
                                {/* Phone icon for calling */}
                                {outboundConfig && lead.phone && lead.phone.trim() !== '' && !isCallInProgress && (
                                    <button
                                        onClick={handleInitiateCall}
                                        className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
                                        title="Click to call"
                                        disabled={isCallInProgress}
                                    >
                                        <Phone className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Main Content Area with Vertical Tabs */}
                    <div className="flex flex-1 overflow-hidden">
                        {/* Vertical Tabs - Left Sidebar */}
                        <div className="w-48 bg-gray-100 border-r shrink-0 overflow-y-auto">
                            <nav className="p-2 space-y-1">
                                {tabs.map((tab) => {
                                    const Icon = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => handleTabChange(tab.id)}
                                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                                                activeTab === tab.id
                                                    ? 'bg-brandColor text-white shadow-md'
                                                    : 'text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            <Icon className="h-5 w-5 shrink-0" />
                                            <span className="text-sm font-medium">{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>

                        {/* Tab Content - Main Area */}
                        <div className="flex-1 overflow-y-auto bg-white">
                    {/* Lead Form Tab - Regular View Only */}
                    {activeTab === 'form' && !isAdminView && (
                        <div className="p-4 max-w-[50%]">
                            <DynamicLeadForm
                                mode="update"
                                leadUuid={lead.uuid}
                                activeUsersForDropdown={activeUsersForDropdown}
                                onSubmitSuccess={() => {
                                    onUpdate?.();
                                    onClose();
                                }}
                                onClose={onClose}
                                refreshData={onUpdate}
                                {...(vistaYsFormReadOnly ? {
                                    readOnly: true,
                                    readOnlyNote: 'This form is view-only. Update this lead in the Vista Form tab.',
                                    labelOverrides: VISTA_YS_FORM_LABELS,
                                } : {})}
                            />
                        </div>
                    )}

                    {/* Lead Data Tab - Admin View Only (Read-only table view) */}
                    {activeTab === 'leaddata' && isAdminView && (
                        <div className="flex flex-col h-full">
                            {/* Fixed Header */}
                            <div className="sticky top-0 z-10 bg-gray-100 border-b border-gray-300 px-6 py-4 shadow-sm">
                                <h2 className="text-lg font-semibold text-gray-800">Client Lead & School Information</h2>
                            </div>

                            <div className="p-6 flex-1 overflow-y-auto">
                            {loadingFullDetails ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor"></div>
                                    <span className="ml-3 text-gray-600">Loading lead details...</span>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Lead Information and School Information side by side */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {/* Core Lead Fields Section */}
                                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                            <div className="bg-gray-100 px-4 py-3 border-b">
                                                <h3 className="text-md font-semibold text-gray-800">Lead Information</h3>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <tbody className="bg-white divide-y divide-gray-100">
                                                        {[
                                                            { label: 'Name', value: fullLeadDetails?.name || lead.name },
                                                            { label: 'Phone', value: formatPhoneDisplay(fullLeadDetails?.phone || lead.phone, fullLeadDetails?.country_code || lead.country_code) },
                                                            { label: 'Email', value: fullLeadDetails?.email || lead.email },
                                                            { label: 'Message', value: fullLeadDetails?.message || lead.message },
                                                            { label: 'Status', value: fullLeadDetails?.statusDisplay || lead.status_name || lead.status },
                                                            { label: 'Source', value: fullLeadDetails?.sourceDisplay || lead.source_name || lead.source },
                                                            { label: 'Grade', value: fullLeadDetails?.gradeDisplay || fullLeadDetails?.gradeOption?.opt_name || lead.grade_name || lead.grade },
                                                            { label: 'Created Date', value: (fullLeadDetails?.created_date || lead.created_date) ? safeFormatDate(fullLeadDetails?.created_date || lead.created_date, 'PPp') : '-' },
                                                            { label: 'Last Updated', value: (fullLeadDetails?.last_updated_date || lead.last_updated_date) ? safeFormatDate(fullLeadDetails?.last_updated_date || lead.last_updated_date, 'PPp') : '-' },
                                                            { label: 'Latest Incoming', value: fullLeadDetails?.latest_incoming_date ? safeFormatDate(fullLeadDetails.latest_incoming_date, 'PPp') : '-' },
                                                            { label: 'Assigned To', value: fullLeadDetails?.assignments?.[0]?.user?.userName || lead.owner_name || '-' },
                                                            { label: 'Is Primary', value: (fullLeadDetails?.is_primary ?? lead.is_primary) ? 'Yes' : 'No' },
                                                            { label: 'Active', value: (fullLeadDetails?.active ?? lead.active) ? 'Yes' : 'No' },
                                                        ].filter(row => row.value && row.value !== '-').map((row, index) => (
                                                            <tr key={row.label} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                                <td className="px-4 py-2 text-sm font-medium text-gray-600 w-1/3">{row.label}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-900">{String(row.value)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* School Information Section */}
                                        {fullLeadDetails?.clientInfo?.school ? (
                                            <div className="bg-white border border-blue-200 rounded-lg overflow-hidden">
                                                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                                                    <h3 className="text-md font-semibold text-blue-800">School Information</h3>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <tbody className="bg-white divide-y divide-gray-100">
                                                            {[
                                                                { label: 'School ID', value: fullLeadDetails.clientInfo.sd_org_id },
                                                                { label: 'School Name', value: fullLeadDetails.clientInfo.school.slate_name },
                                                                { label: 'City', value: fullLeadDetails.clientInfo.school.city_name },
                                                                { label: 'Area', value: fullLeadDetails.clientInfo.school.area_name },
                                                                { label: 'Is Client', value: fullLeadDetails.clientInfo.school.is_client ? 'Yes' : 'No', isTag: true, tagColor: fullLeadDetails.clientInfo.school.is_client ? 'green' : 'gray' },
                                                                { label: 'Claimed', value: fullLeadDetails.clientInfo.school.claimed ? 'Yes' : 'No', isTag: true, tagColor: fullLeadDetails.clientInfo.school.claimed ? 'green' : 'gray' },
                                                                { label: 'Verified', value: fullLeadDetails.clientInfo.school.verified ? 'Yes' : 'No', isTag: true, tagColor: fullLeadDetails.clientInfo.school.verified ? 'green' : 'gray' },
                                                            ].filter(row => row.value).map((row: any, index) => (
                                                                <tr key={row.label} className={index % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}>
                                                                    <td className="px-4 py-2 text-sm font-medium text-gray-600 w-1/3">{row.label}</td>
                                                                    <td className="px-4 py-2 text-sm text-gray-900">
                                                                        {row.isTag ? (
                                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${row.tagColor === 'green' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                                                                {row.value}
                                                                            </span>
                                                                        ) : (
                                                                            String(row.value)
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="bg-white border border-blue-200 rounded-lg overflow-hidden">
                                                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                                                    <h3 className="text-md font-semibold text-blue-800">School Information</h3>
                                                </div>
                                                <div className="p-6 text-center">
                                                    <Building2 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                                                    <p className="text-sm text-gray-500 mb-2">No school attached to this client</p>
                                                    <p className="text-xs text-gray-400">Go to "Attach School" tab to search and attach a school</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Custom Fields Section */}
                                    {fullLeadDetails?.formattedCustomInfo && Object.keys(fullLeadDetails.formattedCustomInfo).length > 0 && (
                                        <div className="bg-white border border-green-200 rounded-lg overflow-hidden">
                                            <div className="bg-green-50 px-4 py-3 border-b border-green-200">
                                                <h3 className="text-md font-semibold text-green-800">Custom Fields</h3>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <tbody className="bg-white divide-y divide-gray-100">
                                                        {Object.entries(fullLeadDetails.formattedCustomInfo).map(([key, fieldData]: [string, any], index) => {
                                                            const displayValue = fieldData.display || fieldData.value || '-';
                                                            if (!displayValue || displayValue === '-' || displayValue === 'please_select') return null;
                                                            // Use col_label if available, otherwise fallback to formatted key
                                                            const labelDisplay = fieldData.col_label || key.replace(/_/g, ' ');
                                                            return (
                                                                <tr key={key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                                    <td className="px-4 py-3 text-sm font-medium text-gray-600 w-1/3">
                                                                        {labelDisplay}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                                        {String(displayValue)}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Marketing Information Section */}
                                    {(fullLeadDetails?.utm_source || fullLeadDetails?.utm_campaign || fullLeadDetails?.utm_medium ||
                                      fullLeadDetails?.fb_campaign_name || fullLeadDetails?.li_campaign_name || fullLeadDetails?.gclid) && (
                                        <div className="bg-white border border-purple-200 rounded-lg overflow-hidden">
                                            <div className="bg-purple-50 px-4 py-3 border-b border-purple-200">
                                                <h3 className="text-md font-semibold text-purple-800">Marketing Information</h3>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <tbody className="bg-white divide-y divide-gray-100">
                                                        {[
                                                            { label: 'UTM Source', value: fullLeadDetails?.utm_source },
                                                            { label: 'UTM Medium', value: fullLeadDetails?.utm_medium },
                                                            { label: 'UTM Campaign', value: fullLeadDetails?.utm_campaign },
                                                            { label: 'UTM Content', value: fullLeadDetails?.utm_content },
                                                            { label: 'UTM Term', value: fullLeadDetails?.utm_term },
                                                            { label: 'FB Campaign', value: fullLeadDetails?.fb_campaign_name },
                                                            { label: 'FB Ad Name', value: fullLeadDetails?.fb_ad_name },
                                                            { label: 'FB Adset', value: fullLeadDetails?.fb_adset_name },
                                                            { label: 'FB Platform', value: fullLeadDetails?.fb_platform },
                                                            { label: 'LinkedIn Campaign', value: fullLeadDetails?.li_campaign_name },
                                                            { label: 'LinkedIn Form', value: fullLeadDetails?.li_form_name },
                                                            { label: 'GCLID', value: fullLeadDetails?.gclid },
                                                            { label: 'FCLID', value: fullLeadDetails?.fclid },
                                                        ].filter(row => row.value).map((row, index) => (
                                                            <tr key={row.label} className={index % 2 === 0 ? 'bg-white' : 'bg-purple-50/30'}>
                                                                <td className="px-4 py-3 text-sm font-medium text-gray-600 w-1/3">{row.label}</td>
                                                                <td className="px-4 py-3 text-sm text-gray-900">{row.value}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* All Remarks Section - Timeline Style */}
                                    {fullLeadDetails?.remarks && fullLeadDetails.remarks.length > 0 && (
                                        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
                                            <div className="bg-orange-50 px-4 py-3 border-b border-orange-200 flex items-center justify-between">
                                                <h3 className="text-md font-semibold text-orange-800">All Remarks</h3>
                                                <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">{fullLeadDetails.remarks.length} total</span>
                                            </div>
                                            <div className="p-4 relative">
                                                {/* Timeline vertical line */}
                                                <div className="absolute left-7 top-4 bottom-4 w-0.5 bg-gray-200"></div>

                                                <div className="space-y-3">
                                                    {fullLeadDetails.remarks.map((remark: any) => {
                                                        const getDotColor = () => {
                                                            switch (remark.remark_type) {
                                                                case 'admin': return 'bg-indigo-500';
                                                                case 'system': return 'bg-gray-400';
                                                                default: return 'bg-yellow-500';
                                                            }
                                                        };
                                                        const getRemarkBadge = () => {
                                                            switch (remark.remark_type) {
                                                                case 'admin': return <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium">Admin</span>;
                                                                case 'system': return <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded font-medium">System</span>;
                                                                default: return null;
                                                            }
                                                        };
                                                        return (
                                                            <div key={remark.uuid} className="relative pl-8">
                                                                {/* Timeline dot */}
                                                                <div className={`absolute left-1.5 top-2 w-2.5 h-2.5 rounded-full ${getDotColor()} ring-2 ring-white`}></div>

                                                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                                                    <p className="text-sm text-gray-900">{remark.remark}</p>
                                                                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                                                        <span>{remark.user?.userName || 'System'}</span>
                                                                        {getRemarkBadge()}
                                                                        <span>•</span>
                                                                        <span>{safeFormatDate(remark.date_remark, 'PPp')}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            </div>
                        </div>
                    )}

                    {/* Admin Form Tab - Admin View Only (Admin Tracking Fields) */}
                    {activeTab === 'adminform' && isAdminView && (
                        <div className="flex flex-col h-full">
                            {/* Fixed Header - Same style as Lead Data */}
                            <div className="sticky top-0 z-10 bg-gray-100 border-b border-gray-300 px-6 py-4 shadow-sm">
                                <h2 className="text-lg font-semibold text-gray-800">Admin Tracking</h2>
                            </div>

                            <div className="p-6 flex-1 overflow-y-auto">
                                <AdminTrackingForm
                                    leadUuid={lead.uuid}
                                    onSubmitSuccess={() => {
                                        onUpdate?.();
                                        // Don't close modal after saving - user may want to continue editing
                                    }}
                                    onClose={onClose}
                                />
                            </div>
                        </div>
                    )}

                    {/* Attach School Tab - Admin View Only when no school attached */}
                    {activeTab === 'attachschool' && isAdminView && !hasSchoolAttached && (
                        <div className="flex flex-col h-full">
                            {/* Fixed Header - Same style as Lead Data */}
                            <div className="sticky top-0 z-10 bg-gray-100 border-b border-gray-300 px-6 py-4 shadow-sm">
                                <h2 className="text-lg font-semibold text-gray-800">Attach School</h2>
                            </div>

                            <div className="p-6 flex-1 overflow-y-auto">
                                {/* Search Controls - Single Line */}
                                <div className="flex items-center gap-3 mb-6">
                                    {/* Cities Dropdown */}
                                    <select
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor text-gray-900 min-w-[180px]"
                                        value={selectedCity || ''}
                                        onChange={(e) => {
                                            setSelectedCity(e.target.value ? Number(e.target.value) : null);
                                            setSchoolSearchResults([]);
                                        }}
                                        onFocus={fetchPopularCities}
                                    >
                                        <option value="">All Cities</option>
                                        {loadingCities ? (
                                            <option disabled>Loading cities...</option>
                                        ) : (
                                            popularCities.map((city) => (
                                                <option key={city.cityId} value={city.cityId}>
                                                    {city.cityName}
                                                </option>
                                            ))
                                        )}
                                    </select>

                                    {/* Search Input */}
                                    <div className="relative flex-1">
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor text-gray-900"
                                            placeholder="Search schools (min 2 characters)..."
                                            value={schoolSearchQuery}
                                            onChange={(e) => setSchoolSearchQuery(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    searchSchools();
                                                }
                                            }}
                                        />
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    </div>

                                    {/* Search Button */}
                                    <button
                                        type="button"
                                        onClick={searchSchools}
                                        disabled={searchingSchools || schoolSearchQuery.length < 2}
                                        className="px-4 py-2 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                                    >
                                        {searchingSchools ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <Search className="h-4 w-4" />
                                                Search
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* Search Results */}
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                                    <h4 className="text-sm font-medium text-gray-700">
                                        {schoolSearchResults.length > 0
                                            ? `Found ${schoolSearchResults.length} schools`
                                            : 'Search Results'}
                                    </h4>
                                </div>
                                <div className="max-h-96 overflow-y-auto">
                                    {schoolSearchResults.length === 0 ? (
                                        <div className="p-8 text-center text-gray-500">
                                            <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                            <p className="text-sm">Search for schools to see results here</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {schoolSearchResults.map((school) => (
                                                <div
                                                    key={school.slate_id}
                                                    className="p-4 hover:bg-gray-50 flex items-start justify-between gap-4"
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <h5 className="text-sm font-medium text-gray-900 truncate">
                                                            {school.slate_name}
                                                        </h5>
                                                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                                            <MapPin className="h-3 w-3" />
                                                            <span>{school.area_name || 'N/A'}, {school.city_name || 'N/A'}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="text-xs text-gray-400">ID: {school.slate_id}</span>
                                                            {school.is_client === 1 && (
                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                                                    Client
                                                                </span>
                                                            )}
                                                            {school.verified === 1 && (
                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                                                    Verified
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAttachSchool(school.slate_id)}
                                                        className="flex-shrink-0 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                                                    >
                                                        Attach
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            </div>
                        </div>
                    )}

                    {/* Assign School Tab - Admin View Only */}
                    {activeTab === 'assignschool' && isAdminView && (
                        <div className="flex flex-col h-full relative">
                            {/* Fixed Header */}
                            <div className="sticky top-0 z-10 bg-gray-100 border-b border-gray-300 px-6 py-4 shadow-sm">
                                <h2 className="text-lg font-semibold text-gray-800">Assign Lead to Schools</h2>
                            </div>

                            <div className="p-6 flex-1 overflow-hidden pb-20">
                                {/* Two Column Layout */}
                                <div className="grid grid-cols-2 gap-6 h-full overflow-hidden">
                                    {/* Left Column - Assign New Schools */}
                                    <div className="flex flex-col h-full overflow-hidden">
                                        <h3 className="text-md font-semibold text-gray-700 mb-3">Assign to New Schools</h3>

                                        {/* Nearby Schools Section */}
                                        <div className="flex-1 flex flex-col min-h-0">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-gray-600">
                                                    {nearbySearchType === 'nearby' ? 'Nearby Schools (within 15km)' :
                                                     nearbySearchType === 'city_based' ? 'Schools in Lead\'s City' :
                                                     'Suggested Schools'}
                                                    {!loadingNearbySchools && nearbySchools.length > 0 && (
                                                        <span className="ml-1 text-gray-400">({nearbySchools.length})</span>
                                                    )}
                                                </span>
                                                {loadingNearbySchools && (
                                                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                                )}
                                            </div>

                                            {/* Filter input for nearby schools */}
                                            {nearbySchools.length > 0 && (
                                                <div className="space-y-2 mb-2">
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            className="w-full px-3 py-1.5 pl-8 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brandColor focus:border-brandColor text-gray-900 text-sm"
                                                            placeholder="Filter nearby schools..."
                                                            value={nearbySchoolsFilter}
                                                            onChange={(e) => setNearbySchoolsFilter(e.target.value)}
                                                        />
                                                        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                                        {nearbySchoolsFilter && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setNearbySchoolsFilter('')}
                                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                            >
                                                                <X className="h-3.5 w-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {/* Client and Claimed Filters */}
                                                    <div className="flex items-center gap-4">
                                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={showClientOnly}
                                                                onChange={(e) => setShowClientOnly(e.target.checked)}
                                                                className="w-3.5 h-3.5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                                            />
                                                            <span className="text-xs text-gray-600">Clients</span>
                                                        </label>
                                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={showClaimedOnly}
                                                                onChange={(e) => setShowClaimedOnly(e.target.checked)}
                                                                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            />
                                                            <span className="text-xs text-gray-600">Claimed</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="border border-gray-200 rounded-lg overflow-hidden flex-1 overflow-y-auto">
                                                {loadingNearbySchools ? (
                                                    <div className="py-6 flex items-center justify-center">
                                                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                                    </div>
                                                ) : nearbySchools.length === 0 ? (
                                                    <div className="py-4 px-3 text-center text-sm text-gray-500 bg-gray-50">
                                                        <MapPin className="h-6 w-6 mx-auto mb-1 text-gray-300" />
                                                        No nearby schools found. Use search below.
                                                    </div>
                                                ) : (
                                                    <div className="divide-y divide-gray-100">
                                                        {nearbySchools
                                                            .filter(school => {
                                                                // Text filter
                                                                const matchesText = !nearbySchoolsFilter ||
                                                                    school.slate_name?.toLowerCase().includes(nearbySchoolsFilter.toLowerCase()) ||
                                                                    school.area_name?.toLowerCase().includes(nearbySchoolsFilter.toLowerCase()) ||
                                                                    school.city_name?.toLowerCase().includes(nearbySchoolsFilter.toLowerCase());

                                                                // Client filter
                                                                const matchesClient = !showClientOnly || school.is_client === 1;

                                                                // Claimed filter
                                                                const matchesClaimed = !showClaimedOnly || school.claimed === 1;

                                                                return matchesText && matchesClient && matchesClaimed;
                                                            })
                                                            .map((school) => (
                                                            <div
                                                                key={school.slate_id}
                                                                className={`p-2 flex items-center gap-2 hover:bg-gray-50 ${
                                                                    selectedSchoolsToAssign.includes(school.slate_id) ? 'bg-brandColor/5' : ''
                                                                }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedSchoolsToAssign.includes(school.slate_id)}
                                                                    onChange={() => toggleSchoolSelection(school.slate_id)}
                                                                    className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor cursor-pointer"
                                                                />
                                                                <div className="flex-1 min-w-0">
                                                                    {school.slate_slug ? (
                                                                        <a
                                                                            href={`https://yellowslate.com/school/${school.slate_slug}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-sm font-medium text-brandColor hover:underline truncate block"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            {school.slate_name}
                                                                        </a>
                                                                    ) : (
                                                                        <h5 className="text-sm font-medium text-gray-900 truncate">
                                                                            {school.slate_name}
                                                                        </h5>
                                                                    )}
                                                                    <div className="text-xs text-gray-500 truncate">
                                                                        {school.area_name || 'N/A'}, {school.city_name || 'N/A'}
                                                                    </div>
                                                                </div>
                                                                {school.distance != null && (
                                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                                                        {school.distance.toFixed(1)} km
                                                                    </span>
                                                                )}
                                                                {school.is_client === 1 && (
                                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                                                        Client
                                                                    </span>
                                                                )}
                                                                {school.claimed === 1 && school.is_client !== 1 && (
                                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                                                        Claimed
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Existing Assignments */}
                                    <div className="flex flex-col h-full overflow-hidden">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-md font-semibold text-gray-700">Existing Assignments</h3>
                                            <button
                                                type="button"
                                                onClick={fetchSchoolAssignments}
                                                className="text-sm text-brandColor hover:underline"
                                            >
                                                Refresh
                                            </button>
                                        </div>

                                        <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden min-h-0">
                                            {loadingAssignments ? (
                                                <div className="flex items-center justify-center py-8 bg-gray-50 h-full">
                                                    <Loader2 className="h-6 w-6 animate-spin text-brandColor" />
                                                    <span className="ml-2 text-gray-600">Loading...</span>
                                                </div>
                                            ) : existingAssignments.length === 0 ? (
                                                <div className="py-8 bg-gray-50 h-full flex flex-col items-center justify-center">
                                                    <Send className="h-10 w-10 mb-2 text-gray-300" />
                                                    <p className="text-sm text-gray-500">No schools assigned yet</p>
                                                </div>
                                            ) : (
                                                <div className="h-full overflow-y-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50 sticky top-0">
                                                            <tr>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">School</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-100">
                                                            {existingAssignments.map((assignment) => (
                                                                <tr key={assignment.uuid}>
                                                                    <td className="px-3 py-2">
                                                                        {assignment.slate_slug ? (
                                                                            <a
                                                                                href={`https://yellowslate.com/school/${assignment.slate_slug}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-sm font-medium text-brandColor hover:underline"
                                                                            >
                                                                                {assignment.school_name}
                                                                            </a>
                                                                        ) : (
                                                                            <div className="text-sm font-medium text-gray-900">{assignment.school_name}</div>
                                                                        )}
                                                                        <div className="text-xs text-gray-400">
                                                                            {assignment.area_name || 'N/A'}, {assignment.city_name || 'N/A'}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        {assignment.assignment_status === 'success' ? (
                                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                                                                <CheckCircle className="h-3 w-3" /> Success
                                                                            </span>
                                                                        ) : assignment.assignment_status === 'failed' ? (
                                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                                                                <XCircle className="h-3 w-3" /> Failed
                                                                            </span>
                                                                        ) : (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                                                                                {assignment.assignment_status}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Fixed Footer with Assign Button */}
                            <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 shadow-lg">
                                <button
                                    type="button"
                                    onClick={handleAssignSchools}
                                    disabled={selectedSchoolsToAssign.length === 0 || assigningSchools}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brandColor text-white text-sm font-medium rounded-lg hover:bg-brandColor/90 focus:outline-none focus:ring-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {assigningSchools ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Assigning...
                                        </>
                                    ) : (
                                        <>
                                            <School className="h-4 w-4" />
                                            Assign to {selectedSchoolsToAssign.length} School{selectedSchoolsToAssign.length !== 1 ? 's' : ''}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Line Items Tab - Only for Ecommerce */}
                    {activeTab === 'lineitems' && isEcommerce && (
                        <div className="p-4">
                            {loadingTabs.has('lineitems') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                    <span className="ml-2 text-gray-600">Loading line items...</span>
                                </div>
                            ) : lineItems.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                    <p>No line items found for this lead</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Line Items Table */}
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variant</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {lineItems.map((item) => (
                                                    <tr key={item.line_item_id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <div className="text-sm font-medium text-gray-900">{item.title}</div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="text-sm text-gray-600">{item.variant_title || '-'}</div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="text-sm text-gray-600">{item.sku || '-'}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <div className="text-sm text-gray-900">₹{Number(item.variant_price || 0).toFixed(2)}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brandColor text-white">
                                                                {item.quantity}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <div className="text-sm font-semibold text-gray-900">₹{Number(item.line_item_total || 0).toFixed(2)}</div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-gray-50">
                                                <tr>
                                                    <td colSpan={5} className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                                                        Grand Total:
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                                                        ₹{lineItems.reduce((sum, item) => sum + Number(item.line_item_total || 0), 0).toFixed(2)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Marketing Data Tab */}
                    {activeTab === 'marketing' && (
                        <div className="p-4">
                            {loadingTabs.has('marketing') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                    <span className="ml-2 text-gray-600">Loading marketing data...</span>
                                </div>
                            ) : (
                                <>
                                <div className="space-y-6">
                                {/* UTM Parameters */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 mb-3">UTM Parameters</h3>
                                    <div className="grid grid-cols-1 gap-3">
                                        {[
                                            { label: 'Source', value: lead.utm_source },
                                            { label: 'Campaign', value: lead.utm_campaign },
                                            { label: 'Medium', value: lead.utm_medium },
                                            { label: 'Content', value: lead.utm_content },
                                            { label: 'Term', value: lead.utm_term },
                                            { label: 'Location', value: lead.utm_location },
                                            { label: 'Device', value: lead.utm_device },
                                            { label: 'Network', value: lead.utm_network },
                                            { label: 'Platform', value: lead.utm_platform },
                                            { label: 'UTM Campaign Id', value: lead.utm_campaign_id },
                                            { label: 'Ad Position', value: lead.utm_adposition }
                                        ].map(({ label, value }) => (
                                            value ? (
                                                <div key={label} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-sm font-medium text-gray-600">{label}:</span>
                                                    <span className="text-sm text-gray-800 font-medium">{value}</span>
                                                </div>
                                            ) : null
                                        ))}
                                    </div>
                                </div>

                                {/* Facebook Data */}
                                {(lead.fb_campaign_name || lead.fb_ad_name || lead.fb_adset_name) && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-800 mb-3">Facebook Marketing</h3>
                                        <div className="grid grid-cols-1 gap-3">
                                            {[
                                                { label: 'Campaign', value: lead.fb_campaign_name },
                                                { label: 'Ad Name', value: lead.fb_ad_name },
                                                { label: 'Ad Set', value: lead.fb_adset_name },
                                                { label: 'Platform', value: lead.fb_platform },
                                                { label: 'Is Organic', value: lead.fb_is_organic }
                                            ].map(({ label, value }) => (
                                                value ? (
                                                    <div key={label} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                        <span className="text-sm font-medium text-gray-600">{label}:</span>
                                                        <span className="text-sm text-gray-800 font-medium">{value}</span>
                                                    </div>
                                                ) : null
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* LinkedIn Data */}
                                {(lead.li_campaign_name || lead.li_form_name) && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-800 mb-3">LinkedIn Marketing</h3>
                                        <div className="grid grid-cols-1 gap-3">
                                            {[
                                                { label: 'Campaign', value: lead.li_campaign_name },
                                                { label: 'Form Name', value: lead.li_form_name }
                                            ].map(({ label, value }) => (
                                                value ? (
                                                    <div key={label} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                        <span className="text-sm font-medium text-gray-600">{label}:</span>
                                                        <span className="text-sm text-gray-800 font-medium">{value}</span>
                                                    </div>
                                                ) : null
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Google Tracking */}
                                {(lead.gclid || lead.fclid) && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-800 mb-3">Google Tracking</h3>
                                        <div className="grid grid-cols-1 gap-3">
                                            {[
                                                { label: 'Google Click ID', value: lead.gclid },
                                                { label: 'Facebook Click ID', value: lead.fclid }
                                            ].map(({ label, value }) => (
                                                value ? (
                                                    <div key={label} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                        <span className="text-sm font-medium text-gray-600">{label}:</span>
                                                        <span className="text-sm text-gray-800 font-mono text-xs break-all">{value}</span>
                                                    </div>
                                                ) : null
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* No marketing data message */}
                                {!lead.utm_source && !lead.utm_campaign && !lead.fb_campaign_name && !lead.li_campaign_name && !lead.gclid && (
                                    <div className="text-center py-8">
                                        <div className="text-gray-400 mb-2">
                                            <BarChart3 size={48} className="mx-auto" />
                                        </div>
                                        <p className="text-gray-500 text-sm">No marketing attribution data available</p>
                                        <p className="text-gray-400 text-xs mt-1">Lead may have come through direct entry or untracked source</p>
                                    </div>
                                )}
                                </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Past Remarks Tab */}
                    {activeTab === 'remarks' && (
                        <div className="p-4">
                            {loadingTabs.has('remarks') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                    <span className="ml-2 text-gray-600">Loading remarks...</span>
                                </div>
                            ) : (
                                <div>
                                    {/* Hide add remark for admin view - admin adds remarks via Admin Form */}
                                    {!isAdminView && (
                                        <div className="mb-4">
                                            <textarea
                                                value={newRemark}
                                                onChange={(e) => setNewRemark(e.target.value)}
                                                placeholder="Add a remark..."
                                                className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brandColor"
                                                rows={3}
                                            />
                                            <button
                                                onClick={handleAddRemark}
                                                disabled={!newRemark.trim()}
                                                className="mt-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brandColor-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Add Remark
                                            </button>
                                        </div>
                                    )}

                                    {/* Timeline Style Remarks */}
                                    {filteredRemarks.length === 0 ? (
                                        <p className="text-gray-500 text-center py-8">No remarks yet</p>
                                    ) : (
                                        <div className="relative">
                                            {/* Timeline vertical line */}
                                            <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                                            <div className="space-y-4">
                                                {filteredRemarks.map((remark) => {
                                                    // Determine dot color based on remark type
                                                    const getDotColor = () => {
                                                        switch (remark.remark_type) {
                                                            case 'audio': return 'bg-brandColor';
                                                            case 'admin': return 'bg-indigo-500';
                                                            case 'system': return 'bg-gray-400';
                                                            default: return 'bg-yellow-500';
                                                        }
                                                    };
                                                    const getRemarkBadge = () => {
                                                        switch (remark.remark_type) {
                                                            case 'admin': return <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium">Admin</span>;
                                                            case 'system': return <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded font-medium">System</span>;
                                                            case 'audio': return <span className="text-xs px-1.5 py-0.5 bg-brandColor/10 text-brandColor rounded font-medium flex items-center gap-1"><Volume2 className="h-3 w-3" />Audio</span>;
                                                            default: return null;
                                                        }
                                                    };
                                                    return (
                                                        <div key={remark.uuid || remark.remark_id} className="relative pl-8">
                                                            {/* Timeline dot */}
                                                            <div className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full ${getDotColor()} ring-2 ring-white`}></div>

                                                            {/* Remark content */}
                                                            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium text-sm text-gray-900">{remark.userDisplay}</span>
                                                                        {getRemarkBadge()}
                                                                    </div>
                                                                    <span className="text-xs text-gray-400">
                                                                        {safeFormatDate(remark.date_remark, 'MMM dd, yyyy HH:mm', 'Just now')}
                                                                    </span>
                                                                </div>
                                                                {remark.remark_type === 'audio' ? (
                                                                    <div className="mt-2">
                                                                        <audio controls className="w-full h-8" preload="metadata">
                                                                            <source src={remark.remark} type="audio/mpeg" />
                                                                            <source src={remark.remark} type="audio/mp3" />
                                                                            <source src={remark.remark} type="audio/wav" />
                                                                            Your browser does not support the audio element.
                                                                        </audio>
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{remark.remark}</p>
                                                                )}
                                                                {remark.next_follow_up && (
                                                                    <p className="text-xs text-brandColor mt-2 flex items-center gap-1">
                                                                        <Clock className="h-3 w-3" />
                                                                        Follow-up: {safeFormatDate(remark.next_follow_up, 'MMM dd, yyyy', 'Not set')}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Activity Tab */}
                    {activeTab === 'activity' && (
                        <div className="p-4">
                            {loadingTabs.has('activity') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                    <span className="ml-2 text-gray-600">Loading activities...</span>
                                </div>
                            ) : activities.length === 0 ? (
                                <div className="text-center py-8">
                                    <div className="text-gray-400 mb-2">
                                        <Activity size={48} className="mx-auto" />
                                    </div>
                                    <p className="text-gray-500 text-sm">No activity recorded yet</p>
                                    <p className="text-gray-400 text-xs mt-1">Activity will appear here as you work with this lead</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {activities.map((activity) => {
                                        // Determine activity type styling
                                        let iconBg = 'bg-gray-100';
                                        let iconColor = 'text-gray-500';
                                        let cardBorder = 'border-gray-200';
                                        
                                        switch (activity.log_type) {
                                            case 'status_change':
                                                iconBg = 'bg-brandColor/10';
                                                iconColor = 'text-brandColor';
                                                cardBorder = 'border-brandColor/20';
                                                break;
                                            case 'field_update':
                                                iconBg = 'bg-green-100';
                                                iconColor = 'text-green-600';
                                                cardBorder = 'border-green-200';
                                                break;
                                            case 'remark_added':
                                                iconBg = 'bg-yellow-100';
                                                iconColor = 'text-yellow-600';
                                                cardBorder = 'border-yellow-200';
                                                break;
                                            case 'assignment':
                                                iconBg = 'bg-purple-100';
                                                iconColor = 'text-purple-600';
                                                cardBorder = 'border-purple-200';
                                                break;
                                            case 'custom':
                                                if (activity.log_action === 'lead_qualified') {
                                                    iconBg = 'bg-emerald-100';
                                                    iconColor = 'text-emerald-600';
                                                    cardBorder = 'border-emerald-200';
                                                }
                                                break;
                                        }

                                        // Function to render field changes from log_data
                                        const renderFieldChanges = (logData: any) => {
                                            if (!logData?.changes || !Array.isArray(logData.changes)) return null;
                                            
                                            // Filter out status changes since they're shown separately with badges
                                            const filteredChanges = logData.changes.filter((change: string) => 
                                                !change.includes('Status changed from')
                                            );
                                            
                                            if (filteredChanges.length === 0) return null;
                                            
                                            return (
                                                <div className="mt-3 space-y-2">
                                                    <p className="text-xs font-medium text-gray-700">Changes Made:</p>
                                                    {filteredChanges.map((change: string, idx: number) => (
                                                        <div key={idx} className="bg-white p-2 rounded border text-xs">
                                                            <div className="text-gray-800">{change}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        };
                                        
                                        return (
                                            <div key={activity.id} className={`bg-white rounded-lg border-l-4 ${cardBorder} shadow-sm hover:shadow-md transition-shadow`}>
                                                <div className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        {/* Activity Icon */}
                                                        <div className={`w-8 h-8 rounded-full ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                                            {activity.log_type === 'status_change' ? (
                                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414L2.586 7l3.707-3.707a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            ) : activity.log_type === 'field_update' ? (
                                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                                </svg>
                                                            ) : activity.log_type === 'assignment' ? (
                                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                                                </svg>
                                                            ) : activity.log_type === 'remark_added' ? (
                                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Activity Content */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-semibold text-gray-900 text-sm">{activity.user_name}</span>
                                                                    <span className="text-gray-600 text-sm">{activity.action}</span>
                                                                </div>
                                                                <span className="text-xs text-gray-500 flex-shrink-0">
                                                                    {safeFormatDate(activity.log_date, 'MMM dd, HH:mm', 'Just now')}
                                                                </span>
                                                            </div>
                                                            
                                                            {/* Activity Details - Skip for status changes and assignments since we show them specially */}
                                                            {activity.details && activity.log_type !== 'status_change' && activity.log_type !== 'assignment' && (
                                                                <p className="text-sm text-gray-700 mb-2">{activity.details}</p>
                                                            )}
                                                            
                                                            {/* Status Change: Show old/new values */}
                                                            {activity.log_type === 'status_change' && activity.log_data && (activity.log_data.old_status || activity.log_data.new_status) && (
                                                                <div className="flex items-center gap-2 mt-2 text-xs">
                                                                    {activity.log_data.old_status && (
                                                                        <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full border border-red-200">
                                                                            {activity.log_data.old_status.name}
                                                                        </span>
                                                                    )}
                                                                    {activity.log_data.old_status && activity.log_data.new_status && (
                                                                        <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                            <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                        </svg>
                                                                    )}
                                                                    {activity.log_data.new_status && (
                                                                        <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full border border-green-200">
                                                                            {activity.log_data.new_status.name}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Assignment Changes: Show owner changes */}
                                                            {activity.log_type === 'assignment' && activity.log_data && (
                                                                <div className="mt-2">
                                                                    {/* Show reassignment from one user to another */}
                                                                    {activity.log_data.previous_owner && activity.log_data.assigned_to_user ? (
                                                                        <div className="flex items-center gap-2 text-sm">
                                                                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                                                                {activity.log_data.previous_owner}
                                                                            </span>
                                                                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                            </svg>
                                                                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-medium">
                                                                                {activity.log_data.assigned_to_user}
                                                                            </span>
                                                                        </div>
                                                                    ) : activity.log_data.assigned_to_user ? (
                                                                        /* Show initial assignment */
                                                                        <div className="flex items-center gap-2 text-sm">
                                                                            <span className="text-gray-600">Assigned to:</span>
                                                                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-medium">
                                                                                {activity.log_data.assigned_to_user}
                                                                            </span>
                                                                        </div>
                                                                    ) : activity.log_data.previous_owner ? (
                                                                        /* Show unassignment */
                                                                        <div className="flex items-center gap-2 text-sm">
                                                                            <span className="text-gray-600">Unassigned from:</span>
                                                                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                                                                {activity.log_data.previous_owner}
                                                                            </span>
                                                                        </div>
                                                                    ) : null}
                                                                    
                                                                    {activity.log_data.assigned_by && (
                                                                        <p className="text-xs text-gray-500 mt-1">
                                                                            By: {activity.log_data.assigned_by}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Field Updates: Show all changes */}
                                                            {activity.log_type === 'field_update' && activity.log_data && renderFieldChanges(activity.log_data)}
                                                            
                                                            {/* Remark Content */}
                                                            {activity.log_type === 'remark_added' && activity.log_data?.remark && (
                                                                <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700 italic">
                                                                    "{activity.log_data.remark}"
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Reminders Tab */}
                    {activeTab === 'reminders' && (
                        <div className="p-4">
                            <div className="space-y-6">
                                {/* Add New Reminder Form */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Set New Reminder</h3>
                                    <ReminderForm
                                        leadUuid={lead?.uuid}
                                        showDelete={false}
                                        isActivity={false}
                                        onCancel={() => {}}
                                        refreshData={() => {
                                            if (lead) {
                                                // Only refresh reminders since we just created one
                                                setLoadedTabs(prev => {
                                                    const newSet = new Set(prev);
                                                    newSet.delete('marketing');
                                                    newSet.delete('reminders');
                                                    return newSet;
                                                });
                                                fetchRemindersOnly();
                                                // Also update activities to show the reminder creation log
                                                setLoadedTabs(prev => {
                                                    const newSet = new Set(prev);
                                                    newSet.delete('activity');
                                                    return newSet;
                                                });
                                                fetchActivitiesOnly();
                                            }
                                        }}
                                    />
                                </div>

                                {/* Existing Reminders */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        Existing Reminders ({reminders.length})
                                    </h3>

                                    {loadingTabs.has('reminders') ? (
                                        <div className="text-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor mx-auto"></div>
                                            <p className="text-gray-500 mt-2">Loading reminders...</p>
                                        </div>
                                    ) : reminders.length === 0 ? (
                                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                                            <div className="text-gray-400 mb-2">
                                                <Bell size={48} className="mx-auto" />
                                            </div>
                                            <p className="text-gray-500 text-sm">No reminders set</p>
                                            <p className="text-gray-400 text-xs mt-1">Set a reminder above to get notified about this lead</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {reminders.map((reminder) => (
                                                <div key={reminder.uuid} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <h4 className="font-medium text-gray-900">{reminder.event_title}</h4>
                                                            <div className="mt-2 space-y-1">
                                                                <p className="text-sm text-gray-600">
                                                                    <span className="font-medium">Date & Time:</span> {safeFormatDate(reminder.db_start, 'PPp')}
                                                                </p>
                                                                <p className="text-sm text-gray-600">
                                                                    <span className="font-medium">Alert:</span> {reminder.alert_before} before
                                                                </p>
                                                                <p className="text-xs text-gray-400">
                                                                    Created: {safeFormatDate(reminder.created_at, 'PP')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 ml-4">
                                                            <div className="w-8 h-8 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center">
                                                                <Bell size={16} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Runo Calls Tab - Admin View Only */}
                    {activeTab === 'runocalls' && isAdminView && (
                        <div className="p-6">
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Runo Call History</h2>
                                        <p className="text-sm text-gray-500 mt-1">Sales calls made via Runo CRM</p>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <Phone className="h-5 w-5" />
                                        <span>{runoCalls.length} call{runoCalls.length !== 1 ? 's' : ''}</span>
                                    </div>
                                </div>

                                {loadingTabs.has('runocalls') ? (
                                    <div className="text-center py-12">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor mx-auto"></div>
                                        <p className="text-gray-500 mt-3">Loading Runo calls...</p>
                                    </div>
                                ) : runoCalls.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                                        <Phone size={56} className="mx-auto mb-3 text-gray-300" />
                                        <p className="text-gray-600 font-medium">No Runo calls yet</p>
                                        <p className="text-gray-400 text-sm mt-1">Runo call logs will appear here after sales calls are made</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {runoCalls.map((call) => (
                                            <div
                                                key={call.id}
                                                className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                            >
                                                <div className="p-4">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start gap-3">
                                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                                call.call_status === 'answered' || call.call_status === 'completed'
                                                                    ? 'bg-green-100 text-green-600'
                                                                    : call.call_status === 'missed'
                                                                    ? 'bg-red-100 text-red-600'
                                                                    : 'bg-gray-100 text-gray-600'
                                                            }`}>
                                                                <Phone className="h-5 w-5" />
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    {call.call_status && (
                                                                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                                                            call.call_status === 'answered' || call.call_status === 'completed'
                                                                                ? 'bg-green-100 text-green-700'
                                                                                : call.call_status === 'missed'
                                                                                ? 'bg-red-100 text-red-700'
                                                                                : 'bg-gray-100 text-gray-700'
                                                                        }`}>
                                                                            {call.call_status.charAt(0).toUpperCase() + call.call_status.slice(1)}
                                                                        </span>
                                                                    )}
                                                                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 font-medium capitalize">
                                                                        {call.call_direction}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-3 text-sm text-gray-600 mt-1 flex-wrap">
                                                                    <span className="font-medium">{call.phone}</span>
                                                                    {call.agent_name && (
                                                                        <>
                                                                            <span className="text-gray-300">•</span>
                                                                            <span>{call.agent_name}</span>
                                                                        </>
                                                                    )}
                                                                    {call.agent_phone && (
                                                                        <>
                                                                            <span className="text-gray-300">•</span>
                                                                            <span className="text-xs text-gray-400">{call.agent_phone}</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            {call.call_duration_sec != null && (
                                                                <p className="text-sm font-medium text-gray-700">
                                                                    {formatDuration(call.call_duration_sec)}
                                                                </p>
                                                            )}
                                                            <p className="text-xs text-gray-400 mt-0.5">
                                                                {call.called_at
                                                                    ? safeFormatDate(call.called_at, 'MMM dd, yyyy · hh:mm a')
                                                                    : safeFormatDate(call.created_at, 'MMM dd, yyyy · hh:mm a')}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {call.recording_url && (
                                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <Mic className="h-4 w-4 text-gray-500" />
                                                                <span className="text-sm font-medium text-gray-700">Recording</span>
                                                            </div>
                                                            <audio
                                                                controls
                                                                className="w-full h-10"
                                                                src={call.recording_url}
                                                                preload="metadata"
                                                            >
                                                                Your browser does not support the audio element.
                                                            </audio>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* AI Calls Tab - Admin View Only */}
                    {activeTab === 'aicalls' && isAdminView && (
                        <div className="p-6">
                            <div className="space-y-6">
                                {/* Header */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">AI Call History</h2>
                                        <p className="text-sm text-gray-500 mt-1">
                                            EchoLeads AI automated calls for this lead
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <Bot className="h-5 w-5" />
                                        <span>{aiCalls.length} call{aiCalls.length !== 1 ? 's' : ''}</span>
                                    </div>
                                </div>

                                {/* Content */}
                                {loadingTabs.has('aicalls') ? (
                                    <div className="text-center py-12">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brandColor mx-auto"></div>
                                        <p className="text-gray-500 mt-3">Loading AI calls...</p>
                                    </div>
                                ) : aiCalls.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                                        <div className="text-gray-400 mb-3">
                                            <Bot size={56} className="mx-auto" />
                                        </div>
                                        <p className="text-gray-600 font-medium">No AI calls yet</p>
                                        <p className="text-gray-400 text-sm mt-1">
                                            AI calls will appear here when triggered for this lead
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {aiCalls.map((call) => {
                                            const isExpanded = expandedCallId === call.id;
                                            return (
                                            <div
                                                key={call.id}
                                                className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                            >
                                                {/* Call Header */}
                                                <button
                                                    type="button"
                                                    className="w-full p-4 cursor-pointer hover:bg-gray-50 text-left"
                                                    onClick={() => setExpandedCallId(isExpanded ? null : call.id)}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                                call.status === 'ended' || call.status === 'completed'
                                                                    ? 'bg-green-100 text-green-600'
                                                                    : call.status === 'failed'
                                                                    ? 'bg-red-100 text-red-600'
                                                                    : 'bg-blue-100 text-blue-600'
                                                            }`}>
                                                                <Phone className="h-5 w-5" />
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-gray-900">
                                                                        {call.direction === 'outboundPhoneCall' ? 'Outbound Call' : call.direction || 'AI Call'}
                                                                    </span>
                                                                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                                                                        call.status === 'ended' || call.status === 'completed'
                                                                            ? 'bg-green-100 text-green-700'
                                                                            : call.status === 'failed'
                                                                            ? 'bg-red-100 text-red-700'
                                                                            : call.status === 'queued'
                                                                            ? 'bg-yellow-100 text-yellow-700'
                                                                            : 'bg-gray-100 text-gray-700'
                                                                    }`}>
                                                                        {call.status}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                                                                    <span>{call.phone_number}</span>
                                                                    {call.duration > 0 && (
                                                                        <>
                                                                            <span className="text-gray-300">•</span>
                                                                            <span>{formatDuration(call.duration)}</span>
                                                                        </>
                                                                    )}
                                                                    {call.agent_name && (
                                                                        <>
                                                                            <span className="text-gray-300">•</span>
                                                                            <span className="text-xs">{call.agent_name}</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-right">
                                                                <p className="text-sm text-gray-500">
                                                                    {call.call_started_at ? safeFormatDate(call.call_started_at, 'PPp') : safeFormatDate(call.created_at, 'PPp')}
                                                                </p>
                                                                {call.ended_reason && (
                                                                    <p className="text-xs text-gray-400 mt-1">
                                                                        {call.ended_reason.replace(/-/g, ' ')}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            {isExpanded ? (
                                                                <ChevronUp className="h-5 w-5 text-gray-400" />
                                                            ) : (
                                                                <ChevronDown className="h-5 w-5 text-gray-400" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>

                                                {/* Expanded Content */}
                                                {isExpanded && (
                                                    <div className="border-t border-gray-100">
                                                        {/* Call Summary */}
                                                        {call.summary && (
                                                            <div className="p-4 bg-blue-50">
                                                                <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                                                                    <Bot className="h-4 w-4" />
                                                                    AI Call Summary
                                                                </h4>
                                                                <p className="text-sm text-blue-800 leading-relaxed">
                                                                    {call.summary}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Recording */}
                                                        {call.recording_url && (
                                                            <div className="p-4 border-t border-gray-100">
                                                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                                                    <Mic className="h-4 w-4" />
                                                                    Call Recording
                                                                </h4>
                                                                <audio
                                                                    controls
                                                                    className="w-full"
                                                                    src={call.recording_url}
                                                                >
                                                                    Your browser does not support the audio element.
                                                                </audio>
                                                            </div>
                                                        )}

                                                        {/* Transcript */}
                                                        {call.transcript && (
                                                            <div className="p-4 border-t border-gray-100">
                                                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                                    Transcript
                                                                </h4>
                                                                <div className="bg-gray-50 rounded-lg p-3 max-h-64 overflow-y-auto">
                                                                    <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans">
                                                                        {call.transcript}
                                                                    </pre>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Call Details */}
                                                        <div className="p-4 border-t border-gray-100 bg-gray-50">
                                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                                <div>
                                                                    <span className="text-gray-500 block">Call ID</span>
                                                                    <span className="text-gray-900 font-mono text-xs">{call.call_id?.slice(0, 12)}...</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500 block">Duration</span>
                                                                    <span className="text-gray-900">{formatDuration(call.duration)}</span>
                                                                </div>
                                                                {call.cost && Number(call.cost) > 0 && (
                                                                    <div>
                                                                        <span className="text-gray-500 block">Cost</span>
                                                                        <span className="text-gray-900">${Number(call.cost).toFixed(4)}</span>
                                                                    </div>
                                                                )}
                                                                {call.call_ended_at && (
                                                                    <div>
                                                                        <span className="text-gray-500 block">Ended At</span>
                                                                        <span className="text-gray-900">{safeFormatDate(call.call_ended_at, 'p')}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'profile' && showLeadProfileTab && lead && (
                        <LeadProfileSections
                            leadUuid={lead.uuid}
                            lead={lead}
                            sectionsEnabled={leadProfileSettings!.sections_enabled}
                            dependentSourceEnabled={leadProfileSettings!.dependent_source_enabled}
                            activeUsersForDropdown={activeUsersForDropdown}
                        />
                    )}

                        </div>
                    </div>
                </div>
            </div>

            {/* Initiate Call Modal */}
            {isCallModalOpen && lead && (
                <InitiateCallModal
                    isOpen={isCallModalOpen}
                    onClose={handleCloseCallModal}
                    leadUuid={lead.uuid}
                    leadName={lead.name || 'Unknown'}
                    leadPhone={formatPhoneForTel(lead.phone, lead.country_code)}
                />
            )}
        </>
    );
};

export default LeadDetailsSidebar;